import { useMutation, useQuery } from '@tanstack/react-query';

import {
  type CreateCalendarEventsInput,
  type FindFreeResourcesFilter,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { toast } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { calendarKeys } from './keys';

const roomLocation = graphql(/* GraphQL */ `
  query calendar_findFreeResources($filter: FindFreeResourcesFilter) {
    calendar_findFreeResources(filter: $filter) {
      freeRooms {
        roomId
        name
      }
      clashingRooms {
        room {
          roomId
          name
        }
      }
    }
  }
`);

const createEvents = graphql(/* GraphQL */ `
  mutation calendar_createCalendarEvents($input: CreateCalendarEventsInput!) {
    calendar_createCalendarEvents(input: $input) {
      success
      clashes {
        eventId
        event
        startTime
        endTime
        clashingRooms {
          roomId
          name
        }
        clashingParties {
          __typename
          ... on Staff {
            partyId
            person {
              partyId
              firstName
              lastName
              avatarUrl
              type
              title {
                id
                name
                nameTextId
              }
            }
          }
        }
      }
    }
  }
`);

const roomLocationQuery = (filter: FindFreeResourcesFilter) => ({
  queryKey: calendarKeys.roomLocation(filter),
  queryFn: () => gqlClient.request(roomLocation, { filter }),
});

export function useRoomLocation(filter: FindFreeResourcesFilter) {
  return useQuery({
    ...roomLocationQuery(filter),
    select: ({ calendar_findFreeResources }) => calendar_findFreeResources,
    enabled: !!filter.recurrence,
  });
}

export function useCreateCalendarEvent() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationKey: calendarKeys.createEvent(),
    mutationFn: async (input: CreateCalendarEventsInput) =>
      gqlClient.request(createEvents, { input }),
    onSuccess: async ({ calendar_createCalendarEvents: calendarData }) => {
      if (calendarData.success) {
        await queryClient.invalidateQueries({ queryKey: calendarKeys.all });
        toast.success(t('common:snackbarMessages.createSuccess'));
      }
    },
  });
}
